// The boat's equipment for a page that shows it or answers about it (TOM-409,
// cloud ADR 0312 §8): the read, read again on every `equipment_changed` frame
// (any owner, any device), and the answer a page has just written.

import type { EquipmentEntry, GearEntry } from '@neomaris/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchEquipment } from './equipment';

export type EquipmentLoad =
  | { kind: 'loading' }
  | { kind: 'ready'; instances: EquipmentEntry[]; gear: GearEntry[] }
  | { kind: 'error' };

export interface UseEquipment {
  load: EquipmentLoad;
  /** Read again. */
  refresh: () => void;
  /** Take an entry the server answered with, in place. */
  apply: (entry: EquipmentEntry) => void;
}

/**
 * `revision` is the boat's `equipmentRevision` (boatChrome): each change reads
 * the list again. `enabled` false reads nothing (a board not being edited).
 */
export function useEquipment(boatId: string, revision: number, enabled = true): UseEquipment {
  const [load, setLoad] = useState<EquipmentLoad>({ kind: 'loading' });
  // A read answers only if nothing newer started since, so a slow read cannot
  // put back what an answer just replaced.
  const seqRef = useRef(0);

  const refresh = useCallback(() => {
    if (boatId === '' || !enabled) return;
    const seq = ++seqRef.current;
    void fetchEquipment(boatId).then(
      (list) => {
        if (seq === seqRef.current) {
          setLoad({ kind: 'ready', instances: list.instances, gear: list.gear ?? [] });
        }
      },
      () => {
        if (seq !== seqRef.current) return;
        // A failed refresh keeps the list already shown.
        setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'error' }));
      }
    );
  }, [boatId, enabled]);

  useEffect(() => {
    setLoad({ kind: 'loading' });
  }, [boatId]);

  useEffect(() => {
    refresh();
  }, [refresh, revision]);

  const apply = useCallback((entry: EquipmentEntry) => {
    seqRef.current++;
    setLoad((prev) =>
      prev.kind !== 'ready'
        ? prev
        : {
            ...prev,
            instances: prev.instances.map((e) => (e.instance === entry.instance ? entry : e)),
          }
    );
  }, []);

  return { load, refresh, apply };
}
